import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { FaFileExcel } from 'react-icons/fa';
import styles from './RegionalMappings.module.css';
import Loader from '../Loader';

const API = process.env.REACT_APP_API_BASE_URL;
const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

/* ─── Accordion item with edit/delete per city ─── */
const AccordionItem = ({ region, cities, isOpen, onClick, onEditCity, onDeleteCity, onAddCity }) => (
    <div className={styles.accordionItem}>
        <button className={styles.accordionHeader} onClick={onClick}>
            <div className={styles.regionInfo}>
                <span className={styles.regionName}>{region}</span>
                <span className={styles.cityCount}>({cities.length} {cities.length === 1 ? 'City' : 'Cities'})</span>
            </div>
            <span className={`${styles.icon} ${isOpen ? styles.open : ''}`}>▼</span>
        </button>
        {isOpen && (
            <div className={styles.accordionContent}>
                <ul>
                    {cities.map(city => (
                        <li key={city.id} className={styles.cityRow}>
                            <span>{city.cityName}</span>
                            <span className={styles.cityActions}>
                                <button className={styles.iconBtn} title="Edit" onClick={() => onEditCity(city)}>✏️</button>
                                <button className={styles.iconBtn} title="Delete" onClick={() => onDeleteCity(city)}>🗑️</button>
                            </span>
                        </li>
                    ))}
                </ul>
                <button className={styles.addCityBtn} onClick={() => onAddCity(region)}>+ Add City</button>
            </div>
        )}
    </div>
);

/* ─── Modal wrapper ─── */
const Modal = ({ title, onClose, children }) => (
    <div className={styles.modalBackdrop}>
        <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
                <h3>{title}</h3>
                <button className={styles.closeBtn} onClick={onClose}>&times;</button>
            </div>
            <div className={styles.modalBody}>{children}</div>
        </div>
    </div>
);

const RegionalMappings = () => {
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Filters
    const [cityFilter, setCityFilter] = useState('');
    const [regionFilter, setRegionFilter] = useState('');

    // Sorting & accordion
    const [regionSortConfig, setRegionSortConfig] = useState({ key: 'name', direction: 'ascending' });
    const [openAccordion, setOpenAccordion] = useState(null);

    // Modal states
    const [showAddRegion, setShowAddRegion] = useState(false);
    const [showEditRegion, setShowEditRegion] = useState(false);
    const [showAddCity, setShowAddCity] = useState(false);
    const [showEditCity, setShowEditCity] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Form fields
    const [formRegionName, setFormRegionName] = useState('');
    const [formRegionCode, setFormRegionCode] = useState('');
    const [formCityName, setFormCityName] = useState('');
    const [formSelectedRegion, setFormSelectedRegion] = useState('');

    // All cities from city management (for dropdown)
    const [allCities, setAllCities] = useState([]);

    // Edit targets
    const [editRegionTarget, setEditRegionTarget] = useState(null); // { regionName, regionCode }
    const [editCityTarget, setEditCityTarget] = useState(null); // { id, cityName, regionName, regionCode }
    const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'city', id, name }

    /* ─── Fetch all mappings ─── */
    const fetchMappings = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/api/mappings`);
            const data = response.data;
            if (data && Array.isArray(data.regionMappings)) {
                setMappings(data.regionMappings);
            } else {
                setError('Received data is not in the expected format.');
            }
        } catch (err) {
            setError('Failed to fetch regional mappings.');
            console.error('Error fetching mappings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchMappings(); }, []);

    // Fetch all cities for the "Add City" dropdown
    useEffect(() => {
        const fetchAllCities = async () => {
            try {
                const res = await axios.get(`${API}/api/cities`);
                setAllCities(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error('Error fetching cities:', err);
            }
        };
        fetchAllCities();
    }, []);

    /* ─── Filtered/sorted data ─── */
    const filteredRegionsForAccordion = useMemo(() => {
        if (!mappings) return [];
        return mappings
            .filter(mapping =>
                mapping.regionName.toLowerCase().includes(cityFilter.toLowerCase()) ||
                (mapping.cities && mapping.cities.some(city => city.cityName.toLowerCase().includes(cityFilter.toLowerCase())))
            )
            .sort((a, b) => a.regionName.localeCompare(b.regionName));
    }, [mappings, cityFilter]);

    const sortedAndFilteredRegionsForTable = useMemo(() => {
        if (!mappings) return [];
        let items = mappings
            .map(m => ({ name: m.regionName, code: m.regionCode }))
            .filter(region => region.name.toLowerCase().includes(regionFilter.toLowerCase()));

        items.sort((a, b) => {
            const valA = regionSortConfig.key === 'code' ? a.code : a.name;
            const valB = regionSortConfig.key === 'code' ? b.code : b.name;
            if (valA < valB) return regionSortConfig.direction === 'ascending' ? -1 : 1;
            if (valA > valB) return regionSortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return items;
    }, [mappings, regionFilter, regionSortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (regionSortConfig.key === key && regionSortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setRegionSortConfig({ key, direction });
    };

    const handleAccordionClick = (regionName) => {
        setOpenAccordion(openAccordion === regionName ? null : regionName);
    };

    /* ─── Region CRUD ─── */
    const handleAddRegion = async () => {
        if (!formRegionName.trim() || !formRegionCode) {
            toast.error('Region name and code are required');
            return;
        }
        // Check for duplicate region code
        const codeNum = Number(formRegionCode);
        const duplicateCode = mappings.find(m => Number(m.regionCode) === codeNum);
        if (duplicateCode) {
            toast.error(`Region code "${codeNum}" is already used by "${duplicateCode.regionName}". Please use a different code.`);
            return;
        }
        // Check for duplicate region name
        const duplicateName = mappings.find(m => m.regionName.toLowerCase() === formRegionName.trim().toLowerCase());
        if (duplicateName) {
            toast.error(`Region "${duplicateName.regionName}" already exists.`);
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/api/mappings/regions`, {
                regionName: formRegionName.trim(),
                regionCode: codeNum
            }, authHeaders());
            toast.success('Region added successfully');
            setShowAddRegion(false);
            resetForm();
            fetchMappings();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to add region');
        } finally {
            setSaving(false);
        }
    };

    const openEditRegionModal = (region) => {
        setEditRegionTarget(region);
        setFormRegionName(region.name);
        setFormRegionCode(region.code);
        setShowEditRegion(true);
    };

    const handleEditRegion = async () => {
        if (!formRegionName.trim() || !formRegionCode) {
            toast.error('Region name and code are required');
            return;
        }
        setSaving(true);
        try {
            await axios.put(
                `${API}/api/mappings/regions/${encodeURIComponent(editRegionTarget.name)}`,
                { regionName: formRegionName.trim(), regionCode: Number(formRegionCode) },
                authHeaders()
            );
            toast.success('Region updated successfully');
            setShowEditRegion(false);
            resetForm();
            fetchMappings();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to update region');
        } finally {
            setSaving(false);
        }
    };

    /* ─── City CRUD ─── */
    const openAddCityModal = (regionName) => {
        const region = mappings.find(m => m.regionName === regionName);
        setFormSelectedRegion(regionName);
        setFormRegionCode(region?.regionCode || '');
        setFormRegionName(regionName);
        setFormCityName('');
        setShowAddCity(true);
    };

    const handleAddCity = async () => {
        if (!formCityName.trim()) {
            toast.error('City name is required');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/api/mappings`, {
                cityName: formCityName.trim(),
                regionName: formSelectedRegion,
                regionCode: Number(formRegionCode)
            }, authHeaders());
            toast.success('City added successfully');
            setShowAddCity(false);
            resetForm();
            fetchMappings();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to add city');
        } finally {
            setSaving(false);
        }
    };

    const openEditCityModal = (city) => {
        // find the region for this city
        const region = mappings.find(m => m.cities.some(c => c.id === city.id));
        setEditCityTarget(city);
        setFormCityName(city.cityName);
        setFormSelectedRegion(region?.regionName || '');
        setFormRegionCode(region?.regionCode || '');
        setShowEditCity(true);
    };

    const handleEditCity = async () => {
        if (!formCityName.trim()) {
            toast.error('City name is required');
            return;
        }
        setSaving(true);
        try {
            const selectedRegion = mappings.find(m => m.regionName === formSelectedRegion);
            await axios.put(`${API}/api/mappings/${editCityTarget.id}`, {
                cityName: formCityName.trim(),
                regionName: formSelectedRegion,
                regionCode: selectedRegion?.regionCode || Number(formRegionCode)
            }, authHeaders());
            toast.success('City updated successfully');
            setShowEditCity(false);
            resetForm();
            fetchMappings();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to update city');
        } finally {
            setSaving(false);
        }
    };

    const openDeleteConfirm = (city) => {
        setDeleteTarget({ type: 'city', id: city.id, name: city.cityName });
        setShowDeleteConfirm(true);
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await axios.delete(`${API}/api/mappings/${deleteTarget.id}`, authHeaders());
            toast.success(`"${deleteTarget.name}" deleted successfully`);
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
            fetchMappings();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to delete');
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setFormRegionName('');
        setFormRegionCode('');
        setFormCityName('');
        setFormSelectedRegion('');
        setEditRegionTarget(null);
        setEditCityTarget(null);
    };

    if (loading) return <div className={styles.loading}>Loading mappings...</div>;
    if (error) return <div className={styles.error}>{error}</div>;

    return (
        <div className={styles.container}>
            {saving && <Loader />}

            {/* ─── Left: City to Region Accordion ─── */}
            <div className={styles.column}>
                <div className={styles.columnHeader}>
                    <h2 className={styles.title}>City to Region Mapping</h2>
                </div>
                <input
                    type="text"
                    placeholder="Filter by city or region name..."
                    className={styles.filterInput}
                    value={cityFilter}
                    onChange={e => setCityFilter(e.target.value)}
                />
                <div className={styles.accordion}>
                    {filteredRegionsForAccordion.map((mapping) => (
                        <AccordionItem
                            key={mapping.regionName}
                            region={mapping.regionName}
                            cities={mapping.cities || []}
                            isOpen={openAccordion === mapping.regionName}
                            onClick={() => handleAccordionClick(mapping.regionName)}
                            onEditCity={openEditCityModal}
                            onDeleteCity={openDeleteConfirm}
                            onAddCity={openAddCityModal}
                        />
                    ))}
                </div>
            </div>

            {/* ─── Right: Region Table ─── */}
            <div className={styles.column}>
                <div className={styles.columnHeader}>
                    <h2 className={styles.title}>Region to Code Mapping</h2>
                </div>

                {/* ─── Toolbar: search (left) · actions (right) ─── */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '1 1 auto' }}>
                        <input
                            type="text"
                            placeholder="Filter by region name..."
                            className={styles.filterInput}
                            value={regionFilter}
                            onChange={e => setRegionFilter(e.target.value)}
                            style={{ margin: 0, maxWidth: 320 }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button className={styles.addBtn} onClick={() => { resetForm(); setShowAddRegion(true); }}>+ Add Region</button>
                        <button
                            className={styles.addBtn}
                            title="Export to Excel"
                            aria-label="Export to Excel"
                            style={{ background: '#16a34a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '8px 12px' }}
                            onClick={() => {
                                // Flatten: one row per city per region
                                const rows = [];
                                mappings.forEach(m => {
                                    if (m.cities && m.cities.length) {
                                        m.cities.forEach(c => rows.push({
                                            'Region Name': m.regionName,
                                            'Region Code': m.regionCode || '',
                                            'City': c.cityName,
                                        }));
                                    } else {
                                        rows.push({ 'Region Name': m.regionName, 'Region Code': m.regionCode || '', 'City': '' });
                                    }
                                });
                                if (!rows.length) return toast.warn('No data to export.');
                                const ws = XLSX.utils.json_to_sheet(rows);
                                const wb = XLSX.utils.book_new();
                                XLSX.utils.book_append_sheet(wb, ws, 'Regional Mappings');
                                XLSX.writeFile(wb, 'RegionalMappings.xlsx');
                            }}
                        ><FaFileExcel size={20} /></button>
                    </div>
                </div>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('name')} className={styles.sortable}>Region Name</th>
                            <th onClick={() => requestSort('code')} className={styles.sortable}>Assigned Code</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedAndFilteredRegionsForTable.map(region => (
                            <tr key={region.name}>
                                <td>{region.name}</td>
                                <td>{region.code}</td>
                                <td>
                                    <button className={styles.iconBtn} title="Edit Region" onClick={() => openEditRegionModal(region)}>✏️</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ─── Add Region Modal ─── */}
            {showAddRegion && (
                <Modal title="Add New Region" onClose={() => setShowAddRegion(false)}>
                    <div className={styles.formGroup}>
                        <label>Region Name</label>
                        <input type="text" value={formRegionName} onChange={e => setFormRegionName(e.target.value)} placeholder="e.g. Karnataka" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Region Code</label>
                        <input type="number" value={formRegionCode} onChange={e => setFormRegionCode(e.target.value)} placeholder="e.g. 5" />
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.saveBtn} onClick={handleAddRegion} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                        <button className={styles.cancelBtn} onClick={() => setShowAddRegion(false)}>Cancel</button>
                    </div>
                </Modal>
            )}

            {/* ─── Edit Region Modal ─── */}
            {showEditRegion && (
                <Modal title="Edit Region" onClose={() => setShowEditRegion(false)}>
                    <div className={styles.formGroup}>
                        <label>Region Name</label>
                        <input type="text" value={formRegionName} onChange={e => setFormRegionName(e.target.value)} />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Region Code</label>
                        <input type="number" value={formRegionCode} onChange={e => setFormRegionCode(e.target.value)} />
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.saveBtn} onClick={handleEditRegion} disabled={saving}>{saving ? 'Saving...' : 'Update'}</button>
                        <button className={styles.cancelBtn} onClick={() => setShowEditRegion(false)}>Cancel</button>
                    </div>
                </Modal>
            )}

            {/* ─── Add City Modal ─── */}
            {showAddCity && (
                <Modal title={`Add City to "${formSelectedRegion}"`} onClose={() => setShowAddCity(false)}>
                    <div className={styles.formGroup}>
                        <label>Select City</label>
                        <select value={formCityName} onChange={e => setFormCityName(e.target.value)}>
                            <option value="">-- Select a City --</option>
                            {allCities
                                .filter(city => {
                                    // Exclude cities already assigned to any region
                                    const alreadyAssigned = mappings.some(m =>
                                        (m.cities || []).some(c => c.cityName.toLowerCase() === city.name.toLowerCase())
                                    );
                                    return !alreadyAssigned;
                                })
                                .map(city => (
                                    <option key={city.id} value={city.name}>{city.name}</option>
                                ))
                            }
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Region</label>
                        <input type="text" value={formSelectedRegion} disabled />
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.saveBtn} onClick={handleAddCity} disabled={saving || !formCityName}>{saving ? 'Saving...' : 'Save'}</button>
                        <button className={styles.cancelBtn} onClick={() => setShowAddCity(false)}>Cancel</button>
                    </div>
                </Modal>
            )}

            {/* ─── Edit City Modal ─── */}
            {showEditCity && (
                <Modal title="Edit City" onClose={() => setShowEditCity(false)}>
                    <div className={styles.formGroup}>
                        <label>City Name</label>
                        <input type="text" value={formCityName} onChange={e => setFormCityName(e.target.value)} />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Move to Region</label>
                        <select value={formSelectedRegion} onChange={e => {
                            const reg = mappings.find(m => m.regionName === e.target.value);
                            setFormSelectedRegion(e.target.value);
                            setFormRegionCode(reg?.regionCode || '');
                        }}>
                            {mappings.map(m => (
                                <option key={m.regionName} value={m.regionName}>{m.regionName}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.saveBtn} onClick={handleEditCity} disabled={saving}>{saving ? 'Saving...' : 'Update'}</button>
                        <button className={styles.cancelBtn} onClick={() => setShowEditCity(false)}>Cancel</button>
                    </div>
                </Modal>
            )}

            {/* ─── Delete Confirmation ─── */}
            {showDeleteConfirm && deleteTarget && (
                <Modal title="Confirm Delete" onClose={() => setShowDeleteConfirm(false)}>
                    <p>Are you sure you want to delete <strong>"{deleteTarget.name}"</strong>?</p>
                    <div className={styles.modalFooter}>
                        <button className={styles.deleteBtn} onClick={handleDelete} disabled={saving}>{saving ? 'Deleting...' : 'Delete'}</button>
                        <button className={styles.cancelBtn} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default RegionalMappings;
